import { createContext, useContext } from 'react';

export type EditLock =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'unlocked' }
  | { status: 'locked'; reason: string };

export const LOADING: EditLock = { status: 'loading' };
export const UNAVAILABLE: EditLock = { status: 'unavailable' };
export const UNLOCKED: EditLock = { status: 'unlocked' };
export const locked = (reason: string): EditLock => ({
  status: 'locked',
  reason,
});

/** What is locked on the thing the viewer is showing. */
export type ViewerEditLocks = {
  /** Lock based on part data */
  partLock: EditLock;
  /**
   * The file's geometry (contours, DXF mapping): the backend's
   * `PartFile.geometry_lock_reason`, which also covers nesting and files shared
   * across parts or quotes. The reason is a clause ("the part is in a nest").
   */
  geometryLock: EditLock;
  /** Lock based on another user is editing a related entity (e.g. quote item). */
  collaborationLock: EditLock;
};

/**
 * The part lock from the flags on a loaded, owned viewer payload. Loading and
 * payloads without these flags are the host's to resolve.
 */
export const partLockFromFlags = ({
  isModifiable,
  isInAFinalizedQuote,
  isInATrashedQuote,
}: {
  isModifiable: boolean;
  isInAFinalizedQuote: boolean;
  isInATrashedQuote: boolean;
}): EditLock => {
  if (!isModifiable) {
    return locked('Cannot edit a part associated with multiple quotes.');
  }
  if (isInAFinalizedQuote) {
    return locked('Cannot edit a part associated with a finalized quote.');
  }
  if (isInATrashedQuote) {
    return locked('Cannot edit a part associated with a trashed quote.');
  }
  return UNLOCKED;
};

/**
 * The collaboration lock from the quote item lock flag.
 */
export const collaborationLockFromFlag = (isQuoteItemLocked: boolean) =>
  isQuoteItemLocked
    ? locked('Cannot edit a part while someone else is editing it.')
    : UNLOCKED;

export const ViewerEditLocksContext = createContext<ViewerEditLocks | null>(
  null,
);

export const useViewerEditLocks = (): ViewerEditLocks => {
  const value = useContext(ViewerEditLocksContext);
  if (value === null) {
    throw new Error('ViewerEditLocks must be provided by the viewer host');
  }
  return value;
};
